const SCHOOL_UTC_OFFSET_MILLISECONDS = 4 * 60 * 60 * 1000;

export const SCHOOL_UTC_OFFSET_MINUTES = 4 * 60;

export interface SchoolDateParts {
  year: number;
  month: number;
  day: number;
  weekday: number;
  dateKey: string;
}

function validInstant(value: Date | string): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function shiftedSchoolDate(value: Date | string): Date | null {
  const date = validInstant(value);
  return date ? new Date(date.getTime() + SCHOOL_UTC_OFFSET_MILLISECONDS) : null;
}

function dateKeyFromParts(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Fixed UTC+4 calendar parts for an absolute instant. */
export function schoolDateParts(value: Date | string): SchoolDateParts | null {
  const shifted = shiftedSchoolDate(value);
  if (!shifted) return null;
  const year = shifted.getUTCFullYear();
  const month = shifted.getUTCMonth() + 1;
  const day = shifted.getUTCDate();
  return {
    year,
    month,
    day,
    weekday: shifted.getUTCDay(),
    dateKey: dateKeyFromParts(year, month, day),
  };
}

export function schoolDateKey(value: Date | string): string | null {
  return schoolDateParts(value)?.dateKey ?? null;
}

export function schoolMinutes(value: Date | string): number | null {
  const shifted = shiftedSchoolDate(value);
  return shifted ? shifted.getUTCHours() * 60 + shifted.getUTCMinutes() : null;
}

/** Absolute instant representing midnight at the school on a `YYYY-MM-DD` date. */
export function schoolDayFromDateKey(dateKey: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const calendarCheck = new Date(Date.UTC(year, month - 1, day));
  if (
    calendarCheck.getUTCFullYear() !== year ||
    calendarCheck.getUTCMonth() !== month - 1 ||
    calendarCheck.getUTCDate() !== day
  ) {
    return null;
  }
  return new Date(calendarCheck.getTime() - SCHOOL_UTC_OFFSET_MILLISECONDS);
}

export function schoolInstantFromDateMinutes(dateKey: string, minutes: number): Date | null {
  if (!Number.isInteger(minutes) || minutes < 0 || minutes >= 24 * 60) return null;
  const day = schoolDayFromDateKey(dateKey);
  return day ? new Date(day.getTime() + minutes * 60_000) : null;
}

export function startOfSchoolDay(value: Date): Date {
  const dateKey = schoolDateKey(value);
  const day = dateKey ? schoolDayFromDateKey(dateKey) : null;
  if (!day) throw new RangeError('Schedule day must be a valid date');
  return day;
}

export function addSchoolDays(value: Date, amount: number): Date {
  const parts = schoolDateParts(value);
  if (!parts || !Number.isFinite(amount)) throw new RangeError('Schedule day must be valid');
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + amount));
  const dateKey = dateKeyFromParts(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth() + 1,
    shifted.getUTCDate(),
  );
  return schoolDayFromDateKey(dateKey)!;
}

export function addSchoolMonths(value: Date, amount: number): Date {
  const parts = schoolDateParts(value);
  if (!parts || !Number.isFinite(amount)) throw new RangeError('Schedule day must be valid');
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1 + amount, 1));
  const dateKey = dateKeyFromParts(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth() + 1,
    shifted.getUTCDate(),
  );
  return schoolDayFromDateKey(dateKey)!;
}

export function startOfSchoolWeek(value: Date): Date {
  const parts = schoolDateParts(value);
  if (!parts) throw new RangeError('Schedule day must be valid');
  return addSchoolDays(startOfSchoolDay(value), -((parts.weekday + 6) % 7));
}

/** Locale formatting pinned to the fixed UTC+4 wall calendar. */
export function formatSchoolDate(
  value: Date | string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string {
  const shifted = shiftedSchoolDate(value);
  if (!shifted) return '';
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(shifted);
}
